import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import { CONFIG_VARS } from "../../constants/configuration";
import { phoneNumberParserUtils } from "../../utils/phoneNumber-parser-utils";

const { baseUrl, phoneBaseUrl, headers, apiVersion } = CONFIG_VARS;

export const authApi = createApi({
  reducerPath: "authApi",
  baseQuery: fetchBaseQuery({
    headers,
    prepareHeaders: (headers, { getState }) => {
      const token = getState().auth?.tokens?.access_token;
      if (token) {
        headers.set("Authorization", `${token}`);
      }
      headers.set("accept", "application/json");
      headers.set("product-id", "100");
      headers.set("tenant-id", "99999");
      headers.set("Content-Type", "application/json");
      return headers;
    }
  }),
  endpoints: (build) => ({
    signupSchool: build.mutation({
      query: (data) => ({
        url: `${baseUrl["1"]}/${apiVersion}/onboarding/school-register`,
        method: "POST",
        body: {
          first_name: data.first_name,
          ...(data.lastName != "" ? { last_name: data.lastName } : ""),
          primary_contact_info: {
            name: data.primary_contact_info.name,
            phone: data.primary_contact_info.phone,
            isd_code: data.primary_contact_info.isd_code,
            designation: "School Owner"
          },
          school_name: data.school_name,
          email: data.email,
          whatsapp_consent: data.whatsappConsent,
          activation_code: data.activation_code,
          udise_code: data.udise_code
        }
      })
    }),
    requestCode: build.mutation({
      query: (data) => ({
        url: `${baseUrl["1"]}/${apiVersion}/request-activation-code`,
        method: "POST",
        body: {
          first_name: data.firstName,
          ...(data.lastName != "" ? { last_name: data.lastName } : ""),
          phone: data.phoneNumber,
          isd_code: data.isdCode,
          school_name: data.schoolName,
          email: data.email,
          whatsapp_consent: data.whatsappConsent
        }
      })
    }),
    signupSchoolFinish: build.mutation({
      query: (data) => ({
        url: `${baseUrl["1"]}/${apiVersion}/onboarding/school-onboard`,
        method: "POST",
        body: {
          ...data
        }
      })
    }),
    loginSchool: build.mutation({
      query: (data) => ({
        url: `${baseUrl["1"]}/${apiVersion}/onboarding/school-login`,
        method: "POST",
        body: {
          ...data
        }
      })
    }),
    loginWithOtpSchool: build.mutation({
      query: (data) => ({
        url: `${baseUrl["1"]}/${apiVersion}/school/login-with-otp`,
        method: "POST",
        body: {
          otp: data.otpCode,
          phone: data.phoneNumber,
          isd_code: data.isdCode
        }
      })
    }),
    validateInvitationCode: build.mutation({
      query: (data) => ({
        url: `${baseUrl["1"]}/${apiVersion}/activation-codes/validate`,
        method: "POST",
        body: {
          code: data
        }
      })
    }),
    verifyPhoneNumber: build.mutation({
      query: (data) => ({
        url: `${baseUrl["1"]}/${apiVersion}/onboarding/school/verify-phone/${data}`,
        method: "GET"
      })
    }),
    verifyEmail: build.mutation({
      query: (data) => ({
        url: `${baseUrl["1"]}/${apiVersion}/onboarding/school/verify-email/${data}`,
        method: "GET"
      })
    }),
    getAllGrades: build.query({
      query: () => ({
        url: `${baseUrl["1"]}/${apiVersion}/onboarding/student-grades`,
        params: {}
      })
    }),
    studentSignUp: build.mutation({
      query: (data) => ({
        url: `${baseUrl["1"]}/${apiVersion}/onboarding/student-register-v2`,
        method: "POST",
        body: {
          first_name: data.first_name,
          last_name: data.lastName || "",
          whatsapp_consent: data.whatsapp_consent !== undefined ? data.whatsapp_consent : data.remember,
          phone: data.phone,
          isd_code: data.isd_code,
          grade_id: data.grade_id || data.gradeCode,
          school_code: data.school_code || data.invitationCode,
          validate_account: true
        }
      })
    }),
    studentSignUpDeeplink: build.mutation({
      query: (data) => ({
        url: `${baseUrl["1"]}/${apiVersion}/onboarding/registerDeeplink`,
        method: "POST",
        body: {
          first_name: data.first_name,
          last_name: data.lastName || "",
          phone: data.phone,
          isd_code: data.isd_code,
          grade_id: data.grade_id,
          school_code: data.school_code || data.invitationCode,
          validate_account: true,
          ...(data.event_name && { event_name: data.event_name }),
          ...(data.level && { level: data.level }),
          ...(data.round && { round: data.round }),
          ...(data.preferred_exam && { preferred_exam: data.preferred_exam })
        }
      })
    }),
    validateDeeplinkEvent: build.mutation({
      query: (data) => ({
        url: `${baseUrl["1"]}/${apiVersion}/events/validateEvent`,
        method: "POST",
        body: {
          school_id: data.school_id,
          event_name: data.event_name,
          level: data.level,
          round: data.round
        }
      })
    }),
    studentLoginAdmissionID: build.mutation({
      query: (data) => ({
        url: `${baseUrl["1"]}/${apiVersion}/onboarding/student-login-v2`,
        method: "POST",
        body: {
          password: data.password.toUpperCase(),
          admission_id: data.admission_id.toUpperCase()
        }
      })
    }),
    studentLoginPassword: build.mutation({
      query: (data) => ({
        url: `${baseUrl["1"]}/${apiVersion}/onboarding/student-login`,
        method: "POST",
        body: {
          password: data.password,
          phone: data.phoneNumber,
          isd_code: data.isdCode
        }
      })
    }),
    studentLoginOTP: build.mutation({
      query: (data) => ({
        url: `${baseUrl["1"]}/${apiVersion}/onboarding/student-login-otp-v2`,
        method: "POST",
        body: {
          otp: data.otpCode,
          phone: data.phoneNumber,
          isd_code: data.isdCode
        }
      })
    }),
    validatePhone: build.mutation({
      query: (data) => ({
        url: `${phoneBaseUrl}/${apiVersion}/auth/validate-phone/user`,
        method: "POST",
        body: {
          phone: data.phoneNumber,
          isd_code: data.isdCode
        }
      })
    }),
    studentSubmitQuestions: build.mutation({
      query: (data) => ({
        url: `${baseUrl["1"]}/${apiVersion}/questionnaires/register-student-response`,
        method: "POST",
        body: data
      })
    }),

    getEvents: build.mutation({
      query: (data) => ({
        url: `${baseUrl["1"]}/${apiVersion}/insights/events`,
        method: "POST",
        body: data
      })
    }),

    getToken: build.mutation({
      query: (data) => ({
        url:
          `${baseUrl["1"]}/${apiVersion}/wizklub/generate-token?` +
          "username=" +
          data.username +
          "&name=" +
          data.name +
          "&grade=" +
          data.grade +
          "&event_name=" +
          data.event_name,
        method: "POST"
      })
    }),
    getCategory: build.mutation({
      query: (data) => ({
        url: `${baseUrl["1"]}/${apiVersion}/Fee-category/category?tier=${data.tier}&feeid=${data.feeId}`,
        method: "GET"
      }),
      headers: async ({ getState }) => {
        const token = getState().auth?.tokens?.access_token;
        if (token) {
          return {
            Authorization: `Bearer ${token}`,
            accept: "application/json",
            "Content-Type": "application/json"
          };
        }
        return {
          accept: "application/json",
          "Content-Type": "application/json"
        };
      }
    }),
    getFeeList: build.mutation({
      query: () => ({
        url: `${baseUrl["1"]}/${apiVersion}/Fee-category/feelist`,
        method: "GET"
      }),
      headers: async ({ getState }) => {
        const token = getState().auth?.tokens?.access_token;
        if (token) {
          return {
            Authorization: `Bearer ${token}`,
            accept: "application/json",
            "Content-Type": "application/json"
          };
        }
        return {
          accept: "application/json",
          "Content-Type": "application/json"
        };
      }
    }),
    getTierList: build.mutation({
      query: () => ({
        url: `${baseUrl["1"]}/${apiVersion}/Fee-category/tierlist`,
        method: "GET"
      }),
      headers: async ({ getState }) => {
        const token = getState().auth?.tokens?.access_token;
        if (token) {
          return {
            Authorization: `Bearer ${token}`,
            accept: "application/json",
            "Content-Type": "application/json"
          };
        }
        return {
          accept: "application/json",
          "Content-Type": "application/json"
        };
      }
    }),
    getUAMToken: build.mutation({
      query: (data) => ({
        url: `${baseUrl["1"]}/${apiVersion}/onboarding/get-uam-token` + data.query,
        method: "GET"
      })
    }),
    resetPassword: build.mutation({
      query: (body) => ({
        url: `${baseUrl["1"]}/${apiVersion}/onboarding/reset-password/user`,
        method: "POST",
        body: body
      })
    }),
    forgotPassword: build.mutation({
      query: (body) => ({
        url: `${baseUrl["1"]}/${apiVersion}/onboarding/forget-password/user`,
        method: "POST",
        body: body
      })
    }),
    getCareerCounsellingLink: build.mutation({
      query: (body) => ({
        url: `${baseUrl["1"]}/${apiVersion}/career_counselling/submit_data`,
        method: "POST",
        body: body
      })
    })
  })
});

export const {
  useSignupSchoolMutation,
  useRequestCodeMutation,
  useSignupSchoolFinishMutation,
  useLoginSchoolMutation,
  useLoginWithOtpSchoolMutation,
  useValidateInvitationCodeMutation,
  useVerifyPhoneNumberMutation,
  useGetAllGradesQuery,
  useStudentSignUpMutation,
  useStudentSignUpDeeplinkMutation,
  useValidateDeeplinkEventMutation,
  useStudentLoginPasswordMutation,
  useValidatePhoneMutation,
  useVerifyEmailMutation,
  useStudentLoginOTPMutation,
  useStudentLoginAdmissionIDMutation,
  useStudentSubmitQuestionsMutation,

  useGetEventsMutation,
  useGetTokenMutation,
  useGetUAMTokenMutation,
  useResetPasswordMutation,
  useForgotPasswordMutation,
  useGetFeeListMutation,
  useGetCategoryMutation,
  useGetTierListMutation,
  useGetCareerCounsellingLinkMutation
} = authApi;
